import React from 'react';
import { useNavigate } from 'react-router';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Badge,
  Button,
  type Column,
  LoadingSkeleton,
} from '@redbax/ui-kit';
import { Plus, Search } from 'lucide-react';
import type { PackagePolicySummary } from '@redbax/shared';
import { usePackagePolicies } from '../api/package-policy-hooks';
import { CreatePolicyDialog } from '../components/create-policy-dialog';

const actionBadgeVariant = (action: string) => {
  switch (action) {
    case 'Block':
      return 'destructive' as const;
    case 'RequireApproval':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
};

const columns = (onNavigate: (eco: string) => void): Column<PackagePolicySummary>[] => [
  {
    key: 'ecosystem',
    header: 'Ecosystem',
    sortable: true,
    render: (row) => (
      <button
        type="button"
        className="font-medium text-text-primary hover:underline"
        onClick={() => onNavigate(row.ecosystem)}
      >
        {row.ecosystem}
      </button>
    ),
  },
  { key: 'name', header: 'Policy Name', sortable: true },
  {
    key: 'isEnabled',
    header: 'Status',
    render: (row) => (
      <Badge variant={row.isEnabled ? 'default' : 'secondary'}>
        {row.isEnabled ? 'Enabled' : 'Disabled'}
      </Badge>
    ),
  },
  {
    key: 'defaultAction',
    header: 'Default Action',
    render: (row) => (
      <Badge variant={actionBadgeVariant(row.defaultAction)}>
        {row.defaultAction}
      </Badge>
    ),
  },
];

export function PackagePolicyPage() {
  const navigate = useNavigate();
  const { data: policies, isLoading } = usePackagePolicies();
  const [showCreate, setShowCreate] = React.useState(false);

  const configuredEcosystems = React.useMemo(
    () => (policies ?? []).map((p) => p.ecosystem),
    [policies],
  );

  const handleNavigate = (ecosystem: string) => {
    navigate(`/package-security/policies/${encodeURIComponent(ecosystem)}`);
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Package Security"
          description="Configure package vulnerability detection and response policies per ecosystem"
        />
        <LoadingSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Package Security"
        description="Configure package vulnerability detection and response policies per ecosystem"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/package-security/vulnerabilities')}>
              <Search className="mr-1 h-4 w-4" />
              Vulnerability Database
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Create Policy
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ecosystem Policies</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns(handleNavigate)}
            data={policies ?? []}
            keyExtractor={(row) => row.id}
            emptyMessage="No package policies configured. Create one to get started."
          />
        </CardContent>
      </Card>

      <CreatePolicyDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        configuredEcosystems={configuredEcosystems}
      />
    </div>
  );
}
